import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Loader2, Slack } from '@digital-twins/design-system/icons'

import { TrialLimitUpgradeModal } from '@/components/billing/TrialLimitUpgradeModal'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/routes/routes'
import { useRouter } from '@/routing/navigation'
import { useStartInSlack } from './useStartInSlack'

/**
 * "Start in Slack" in the web compose modal (Slack Twins plan E6, mocks
 * `3-compose.png`): after twin selection, a Where choice. "In the app" keeps
 * the modal's own start button (`children`); "In Slack" starts the room in
 * the app DM ("Just me") or in a channel Twins and the user share, through
 * `POST /slack-twins/rooms`, then links to the thread. Renders only
 * `children` when the server has no Slack app or the org no workspace.
 */
interface StartInSlackProps {
  /** False for guest, audience-only and picker-only modes. */
  isEnabled: boolean
  populationToken: string | null
  participantPersonaIds: Array<string>
  /** The modal's own "In the app" start button. */
  children: ReactNode
  /** The modal's language and credit chips; they share the Where line. */
  settings?: ReactNode
  /** The Where choice waits until at least one twin is picked. */
  hasParticipants?: boolean
}

interface ChoiceChipProps {
  isSelected: boolean
  isDisabled?: boolean
  onSelect: () => void
  testId: string
  children: ReactNode
}

function ChoiceChip(props: ChoiceChipProps) {
  const { isSelected, isDisabled = false, onSelect, testId, children } = props

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      disabled={isDisabled}
      onClick={onSelect}
      data-testid={testId}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-full border px-3.5 text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isSelected
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border bg-transparent text-muted-foreground hover:border-primary/40',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border',
      )}
    >
      {children}
    </button>
  )
}

export function StartInSlack(props: StartInSlackProps) {
  const {
    isEnabled,
    populationToken,
    participantPersonaIds,
    children,
    settings,
    hasParticipants = true,
  } = props

  const { t } = useTranslation('app')

  const {
    isAvailable,
    where,
    setWhere,
    target,
    setTarget,
    channelId,
    setChannelId,
    selectedChannel,
    channels,
    reloadChannels,
    disconnect,
    isDisconnecting,
    hasDisconnectError,
    isStarting,
    canStart,
    error,
    refusal,
    hasTooManyTwins,
    isRoomLimitOpen,
    trialLimitKind,
    closeRoomLimit,
    started,
    start,
  } = useStartInSlack({ isEnabled, populationToken, participantPersonaIds })
  const router = useRouter()

  const onOpenInSlack = () => {
    if (started?.permalink) {
      window.open(started.permalink, '_blank', 'noopener,noreferrer')
    }
  }

  const onOpenHere = () => {
    if (started) {
      router.push(ROUTES.chatRoom(started.roomToken))
    }
  }

  const onConnect = () => {
    if (channels.status === 'notLinked') {
      window.open(channels.connectUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const settingsRow = settings ? (
    <div
      className="flex flex-wrap items-center gap-2 animate-in fade-in slide-in-from-bottom-1 duration-200"
      data-testid="compose-settings-row"
    >
      {settings}
    </div>
  ) : null

  if (!isEnabled || !isAvailable) {
    return (
      <>
        {settingsRow && <div className="flex justify-center">{settingsRow}</div>}
        {children}
      </>
    )
  }

  if (started) {
    return (
      <div className="space-y-3" data-testid="compose-start-in-slack-started">
        <div className="flex items-start gap-2.5">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
          <div className="min-w-0">
            <Text as="p" size="sm" weight="semibold">
              {started.channelName
                ? t('slackTwins.compose.startedInChannel', { channel: started.channelName })
                : t('slackTwins.compose.startedInDm')}
            </Text>
            <Text as="p" size="sm" color="muted">
              {t('slackTwins.compose.startedBody')}
            </Text>
          </div>
        </div>
        {started.permalink && (
          <Button
            variant="cta"
            onClick={onOpenInSlack}
            className="h-11 w-full rounded-xl text-sm"
            data-testid="compose-start-in-slack-open-link"
          >
            <Slack className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('slackTwins.compose.openInSlack')}
          </Button>
        )}
        <Button
          variant="outline"
          onClick={onOpenHere}
          className="h-11 w-full rounded-xl text-sm"
          data-testid="compose-start-in-slack-open-here"
        >
          {t('slackTwins.compose.openHere')}
        </Button>
      </div>
    )
  }

  const whereChoice = (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          className="flex flex-wrap gap-2"
          role="radiogroup"
          aria-label={t('slackTwins.compose.where')}
          data-testid="compose-start-in-slack-toggle"
        >
          <ChoiceChip
            isSelected={where === 'app'}
            onSelect={() => setWhere('app')}
            testId="compose-start-in-slack-where-app"
          >
            {t('slackTwins.compose.inApp')}
          </ChoiceChip>
          <ChoiceChip
            isSelected={where === 'slack'}
            isDisabled={hasTooManyTwins}
            onSelect={() => setWhere('slack')}
            testId="compose-start-in-slack-where-slack"
          >
            <Slack className="h-4 w-4" aria-hidden="true" />
            {t('slackTwins.compose.inSlack')}
          </ChoiceChip>
        </div>
        {settingsRow}
      </div>
      {hasTooManyTwins && (
        <Text as="p" size="sm" color="muted" data-testid="compose-start-in-slack-too-many">
          {t('slackTwins.compose.tooManyTwins')}
        </Text>
      )}
    </div>
  )

  if (where === 'app') {
    return (
      <div className="space-y-4">
        {hasParticipants && whereChoice}
        {children}
      </div>
    )
  }

  let body: ReactNode = null
  if (channels.status === 'idle' || channels.status === 'loading') {
    body = (
      <div className="flex items-center gap-2" data-testid="compose-start-in-slack-loading">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    )
  } else if (channels.status === 'error') {
    body = (
      <div className="flex items-center justify-between gap-3">
        <Text as="p" size="sm" color="muted" role="alert">
          {t('slackTwins.compose.loadError')}
        </Text>
        <Button
          variant="ghost"
          size="sm"
          onClick={reloadChannels}
          data-testid="compose-start-in-slack-retry"
        >
          {t('slackTwins.compose.retry')}
        </Button>
      </div>
    )
  } else if (channels.status === 'notLinked') {
    body = (
      <div className="space-y-3" data-testid="compose-start-in-slack-not-linked">
        <Text as="p" size="sm" color="muted">
          {t('slackTwins.compose.notLinked')}
        </Text>
        <Text as="p" size="sm" color="muted">
          {t('slackTwins.compose.connectHow')}
        </Text>
        <Button
          variant="cta"
          onClick={onConnect}
          className="h-11 w-full rounded-xl text-sm"
          data-testid="compose-start-in-slack-connect"
        >
          <Slack className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('slackTwins.compose.connect')}
        </Button>
      </div>
    )
  } else {
    const hasChannels = channels.channels.length > 0

    const channelField = hasChannels ? (
      <div className="space-y-2">
        <select
          value={channelId ?? ''}
          onChange={(event) => setChannelId(event.target.value || null)}
          aria-label={t('slackTwins.compose.channelAria')}
          data-testid="compose-start-in-slack-channel-select"
          className="h-10 w-full rounded-[10px] border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">{t('slackTwins.compose.channelPlaceholder')}</option>
          {channels.channels.map((channel) => (
            <option key={channel.channelId} value={channel.channelId}>
              #{channel.name}
            </option>
          ))}
        </select>
        <Text as="p" size="sm" color="muted">
          {t('slackTwins.compose.channelHelp')}
        </Text>
      </div>
    ) : (
      <Text as="p" size="sm" color="muted" data-testid="compose-start-in-slack-no-channels">
        {t('slackTwins.compose.noChannels')}
      </Text>
    )

    let errorLine: ReactNode = null
    if (error === 'channelUnavailable') {
      errorLine = t('slackTwins.compose.channelUnavailable', {
        channel: selectedChannel?.name ?? '',
      })
    } else if (error === 'stillStarting') {
      errorLine = t('slackTwins.compose.stillStarting')
    } else if (error === 'refused' && refusal) {
      errorLine = refusal
    } else if (error === 'startError') {
      errorLine = t('slackTwins.compose.startError')
    }

    let submitLabel = t('slackTwins.compose.start')
    if (isStarting) {
      submitLabel = t('slackTwins.compose.starting')
    } else if (target === 'channel' && selectedChannel) {
      submitLabel = t('slackTwins.compose.startInChannel', { channel: selectedChannel.name })
    }

    body = (
      <div className="space-y-4">
        <div className="space-y-2">
          <Text as="p" size="sm" color="muted">
            {t('slackTwins.compose.whoSees')}
          </Text>
          <div
            className="flex flex-wrap gap-2"
            role="radiogroup"
            aria-label={t('slackTwins.compose.whoSees')}
          >
            <ChoiceChip
              isSelected={target === 'dm'}
              onSelect={() => setTarget('dm')}
              testId="compose-start-in-slack-target-dm"
            >
              {t('slackTwins.compose.justMe')}
            </ChoiceChip>
            <ChoiceChip
              isSelected={target === 'channel'}
              onSelect={() => setTarget('channel')}
              testId="compose-start-in-slack-target-channel"
            >
              {t('slackTwins.compose.channel')}
            </ChoiceChip>
          </div>
          {target === 'dm' ? (
            <Text as="p" size="sm" color="muted">
              {t('slackTwins.compose.justMeHelp')}
            </Text>
          ) : (
            channelField
          )}
        </div>
        {errorLine && (
          <Text
            as="p"
            size="sm"
            color="destructive"
            role="alert"
            data-testid="compose-start-in-slack-error"
          >
            {errorLine}
          </Text>
        )}
        <Button
          variant="cta"
          onClick={() => void start()}
          disabled={!canStart}
          className="h-11 w-full rounded-xl text-sm"
          data-testid="compose-start-in-slack-submit"
        >
          {isStarting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Slack className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          {submitLabel}
        </Button>
        <div className="flex flex-col items-start gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void disconnect()}
            disabled={isDisconnecting}
            data-testid="compose-start-in-slack-disconnect"
          >
            {isDisconnecting ? t('slackTwins.compose.disconnecting') : t('slackTwins.compose.disconnect')}
          </Button>
          {hasDisconnectError && (
            <Text
              as="p"
              size="sm"
              color="destructive"
              role="alert"
              data-testid="compose-start-in-slack-disconnect-error"
            >
              {t('slackTwins.compose.disconnectError')}
            </Text>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {whereChoice}
      {body}
      <TrialLimitUpgradeModal open={isRoomLimitOpen} onClose={closeRoomLimit} kind={trialLimitKind} />
    </div>
  )
}
